import React from "react";
import { useForm, useFormState, useWatch } from "react-hook-form";

import { DateTime } from "luxon";
import useSWR from "swr";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import Edit from "@mui/icons-material/Edit";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { useSort } from "@tzmedical/react-hooks";

import arrayIncludesAny from "../../shared/arrayIncludesAny.js";
import axios, { swrFetcher } from "../axiosClient.js";
import useAuth0Roles from "../hooks/auth0Roles.jsx";
import useAlertSnackbar from "../hooks/useAlertSnackbar.jsx";
import Alert from "./common/Alert.jsx";
import CancelButton from "./common/CancelButton.jsx";
import ColumnHeader from "./common/ColumnHeader.jsx";
import DialogTitleBar from "./common/DialogTitleBar.jsx";
import FormAutocompleteInput from "./common/FormAutocompleteInput.jsx";
import PageLoading from "./common/PageLoading.jsx";
import ConfirmAction from "./ConfirmAction.jsx";

const sortOptions = {
  defaultSort: {
    field: "total",
    reverse: true,
  },
  fieldGetters: {
    completed: (object) => object.projects?.reduce((total, { points }) => total + points.completed, 0),
    inProgress: (object) => object.projects?.reduce((total, { points }) => total + points.inProgress, 0),
  },
};

function EngineeringSprintForm({
  // props
  project,
  sprint,
  mutate,
}) {
  const [open, setOpen] = React.useState(false);

  const [submissionError, setSubmissionError] = React.useState(null);
  // This component doesn't care about the current state of the success message
  const [, setSuccess] = useAlertSnackbar("success", 30_000);
  const [loading, setLoading] = React.useState(false);

  const {
    data: jiraSprints,
    error: jiraSprintsError,
    isLoading: jiraSprintsLoading,
  } = useSWR(open ? { url: "/api/jira/sprints" } : null, swrFetcher, {
    fallbackData: sprint ? [sprint] : [],
  });
  const {
    data: databaseSprints,
    error: databaseSprintsError,
    isLoading: databaseSprintsLoading,
  } = useSWR(open ? { url: "/api/engineering-sprints/" } : null, swrFetcher);

  const sprintsLoading = jiraSprintsLoading || databaseSprintsLoading;

  const sprintOptions = React.useMemo(
    () =>
      jiraSprints
        .filter(({ id }) => !databaseSprints?.find((s) => s.id === id))
        .map(({ id, name, endDate, startDate }) => ({
          id,
          name,
          startDate,
          endDate,
        })),
    [databaseSprints, jiraSprints]
  );

  const formTitle = React.useMemo(() => (sprint?.id ? "Update Sprint" : "Add Sprint"), [sprint?.id]);
  const disablePropagation = React.useCallback((event) => {
    event.stopPropagation();
  }, []);

  const defaultValues = React.useMemo(
    () => ({
      sprint: sprintOptions.find((option) => option.id === sprint?.id) || "",
    }),
    [sprint?.id, sprintOptions]
  );

  const { handleSubmit, control, reset } = useForm({
    defaultValues,
    shouldUnregister: true,
  });
  const { isDirty } = useFormState({ control });

  const sprintWatch =
    useWatch({ control, name: "sprint" }) || sprintOptions.find((option) => option.id === sprint?.id);

  const {
    data: userWork,
    error: userWorkError,
    isLoading: workLoading,
  } = useSWR(open && sprintWatch?.id ? { url: `/api/jira/sprint/${sprintWatch.id}` } : null, swrFetcher);

  const combinedSwrError = jiraSprintsError || databaseSprintsError || userWorkError;
  const error =
    submissionError ||
    (combinedSwrError ? combinedSwrError.response?.data?.error || combinedSwrError.message : null);

  const [sortedWork, handleSortSelection, sort] = useSort(userWork || [], sortOptions);

  const handleOpen = React.useCallback(
    (event) => {
      event.stopPropagation();
      setSubmissionError(null);
      setOpen(true);
      reset(defaultValues);
    },
    [defaultValues, reset]
  );

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const onSubmit = React.useCallback(
    async (data) => {
      setLoading(true);

      if (data.sprint?.id) {
        data.id = data.sprint.id;
        data.name = data.sprint.name;
        data.startDate = DateTime.fromISO(data.sprint.startDate).toISODate();
        data.endDate = DateTime.fromISO(data.sprint.endDate).toISODate();
      }
      const payload = {
        ...data,
        work: userWork,
      };

      try {
        if (sprint) {
          await axios({
            method: "PATCH",
            url: `/api/engineering-sprints/${sprint.id}`,
            data: payload,
          });
        } else {
          await axios({
            method: "POST",
            url: `/api/engineering-sprints`,
            data: payload,
          });
        }

        handleClose();
        if (mutate) {
          mutate();
        }
        setSuccess(`Successfully ${sprint ? "updated" : "added"} sprint.`);
      } catch (err) {
        setSubmissionError(err.response?.data?.error || err.message);
      }
      setLoading(false);
    },
    [userWork, sprint, handleClose, mutate, setSuccess]
  );

  const { roles } = useAuth0Roles();
  const canDelete = React.useMemo(
    () => arrayIncludesAny(roles, ["Scorecard Admin", "Scorecard PLC Admin"]),
    [roles]
  );
  const handleDeleteConfirmation = React.useCallback(async () => {
    if (sprint) {
      await axios({
        method: "DELETE",
        url: `/api/engineering-sprints/${sprint.id}`,
      });
      handleClose();
      if (mutate) {
        mutate();
      }
    }
  }, [sprint, handleClose, mutate]);

  return (
    <>
      {sprint ? (
        <Tooltip title="Edit this sprint" arrow>
          <IconButton
            aria-label="edit-sprint"
            color="primary"
            onClick={handleOpen}
            data-cy={`edit-sprint-${sprint.id}`}
          >
            <Edit />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Add a sprint" arrow>
          <IconButton aria-label="add-sprint" color="secondary" onClick={handleOpen} data-cy="add-sprint">
            <AddCircleIcon />
          </IconButton>
        </Tooltip>
      )}
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClick={disablePropagation}
        data-cy="sprint-form"
        slotProps={{
          paper: {
            component: "form",
            onSubmit: handleSubmit(onSubmit),
            noValidate: true,
            sx: { minHeight: "70vh" },
          },
        }}
      >
        <Alert message={error} setMessage={setSubmissionError} level="error" data-cy="dialog-error" />
        <DialogTitleBar title={formTitle} Icon={DirectionsRunIcon} iconColor="secondary" />
        <DialogContent>
          {sprintsLoading ? (
            <PageLoading />
          ) : (
            <>
              <Grid container spacing={4} sx={{ alignItems: "center" }}>
                <Grid size={12}>
                  <FormAutocompleteInput
                    control={control}
                    name="sprint"
                    label="Sprint ID"
                    required
                    options={sprintOptions}
                    disabled={!!sprint}
                    rules={{ required: "Please provide an ID for this sprint" }}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {!sprintsLoading && workLoading && <PageLoading />}
          {!sprintsLoading && !workLoading && userWork?.length > 0 && (
            <TableContainer component={Paper} sx={{ mt: 4 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={(theme) => ({ backgroundColor: theme.palette.primary[theme.palette.mode] })}>
                    <TableCell>
                      <ColumnHeader
                        id="user"
                        display="Name"
                        color="primary"
                        sortField={sort.field}
                        reverseSort={sort.reverse}
                        onClick={handleSortSelection}
                      />
                    </TableCell>
                    <TableCell>
                      <ColumnHeader
                        id="total"
                        display="Total Points"
                        color="primary"
                        sortField={sort.field}
                        reverseSort={sort.reverse}
                        onClick={handleSortSelection}
                      />
                    </TableCell>
                    <TableCell>
                      <ColumnHeader
                        id="completed"
                        display="Completed Points"
                        color="primary"
                        sortField={sort.field}
                        reverseSort={sort.reverse}
                        onClick={handleSortSelection}
                      />
                    </TableCell>
                    <TableCell>
                      <ColumnHeader
                        id="inProgress"
                        display="In Progress Points"
                        color="primary"
                        sortField={sort.field}
                        reverseSort={sort.reverse}
                        onClick={handleSortSelection}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedWork.map((work) => (
                    <TableRow key={work.user}>
                      <TableCell>{work.user}</TableCell>
                      <TableCell>{work.total}</TableCell>
                      <TableCell>
                        {work.projects.map((p) => (
                          <Typography
                            sx={{ fontSize: "inherit" }}
                            key={`${work.user}-${p.project.name}-completed`}
                          >
                            {`${p.project.name}: ${p.points.completed}`}
                          </Typography>
                        ))}
                      </TableCell>
                      <TableCell>
                        {work.projects.map((p) => (
                          <Typography
                            sx={{ fontSize: "inherit" }}
                            key={`${work.user}-${p.project.name}-inProgress`}
                          >
                            {`${p.project.name}: ${p.points.inProgress}`}
                          </Typography>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ m: 2, mr: "auto" }}>
            {!!sprint && canDelete && (
              <ConfirmAction
                actionName="Delete"
                objectName="Sprint"
                handleConfirmation={handleDeleteConfirmation}
                confirmKeyword="DELETE"
              />
            )}
          </Box>
          <Box sx={{ m: 2 }}>
            <CancelButton color="secondary" isDirty={isDirty} onClick={handleClose}>
              Cancel
            </CancelButton>
          </Box>
          <Box sx={{ m: 2 }}>
            <Button
              data-cy="submit-sprint"
              disabled={loading || sprintsLoading || workLoading}
              variant="contained"
              color="secondary"
              loading={loading}
              type="submit"
            >
              {sprint ? "Update" : "Add"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EngineeringSprintForm;
